import React, { useState, useEffect, useRef } from 'react';

interface FilterOption {
  value?: string;
  label?: string;
  hidden?: boolean;
  chainId?: string | number;
  rpc?: string;
  network?: string;
  chainName?: string;
  net?: string;
  weight?: number;
  isTest?: boolean;
  [key: string]: any;
}

interface Props {
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  filterOptions?: FilterOption[];
  filterValue?: string;
  onFilterChange?: (value: string) => void;
  showFilter?: boolean;
  className?: string;
}

function SearchFilterBar({
  searchPlaceholder = "Instant search..",
  searchValue,
  onSearchChange,
  filterOptions = [],
  filterValue,
  onFilterChange,
  showFilter = true,
  className = ""
}: Props) {
  const [localSearchValue, setLocalSearchValue] = useState(searchValue || '');
  const [localFilterValue, setLocalFilterValue] = useState(filterValue || 'any');
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { if (searchValue !== undefined) setLocalSearchValue(searchValue); }, [searchValue]);
  useEffect(() => { if (filterValue !== undefined) setLocalFilterValue(filterValue); }, [filterValue]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const debouncedSearch = (value: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => { if (onSearchChange) onSearchChange(value); }, 500);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => { const v = e.target.value; setLocalSearchValue(v); debouncedSearch(v); };
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter' && onSearchChange) onSearchChange(localSearchValue); };

  const pickFilter = (value: string) => { setLocalFilterValue(value); setOpen(false); if (onFilterChange) onFilterChange(value); };

  const options = (filterOptions || []).filter((o) => !o.hidden);
  const longestLabel = options.reduce((m, o) => {
    const label = o.label || (o.rpc ? o.chainName : 'All');
    return label.length > m ? label.length : m;
  }, 0);
  const selectWidth = Math.max(210, longestLabel * 0.65);

  const getLabel = () => {
    const opt = options.find((o) => {
      const val = o.value ? o.value : o.chainId ? String(o.chainId) : o.rpc || "any";
      return val === localFilterValue || o.net === localFilterValue;
    });
    if (!opt) return 'All';
    return opt.label || (opt.rpc ? opt.chainName : 'All');
  };

  const chainLogoFor = (option: FilterOption | undefined) => {
    if (!option) return null;
    const id = option.chainId ?? option.net;
    if (id == null) return null;
    return `/assets/img/chain-logos/chain-${id}.png`;
  };

  const mainnets = options.filter((o) => !o.isTest);
  const tests = options.filter((o) => o.isTest);
  const ordered = (mainnets.length ? mainnets : options).slice().sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0));
  const testOrdered = (tests.length ? tests : []).slice().sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0));

  return (
    <div className={`filter-bar-wrapper ${className}`}>
      <div className="filter-bar-input-group d-flex align-items-center">
        <input
          type="text"
          className="filter-search-input flex-grow-1"
          value={localSearchValue}
          onChange={handleSearchChange}
          onKeyPress={handleKeyPress}
          placeholder={searchPlaceholder}
          aria-label="Search"
        />
        {showFilter && filterOptions.length > 0 && (
          <div className="search-filter-dropdown" ref={dropdownRef}>
            <button
              type="button"
              className={`search-filter-dropdown-toggle ${open ? 'open' : ''}`}
              onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
              aria-expanded={open}
              style={{ width: `${selectWidth}px` }}
            >
              {getLabel()}
            </button>
            <div
              className="search-filter-dropdown-menu"
              style={{ opacity: open ? 1 : 0, transform: open ? 'translateY(0)' : 'translateY(-4px)', pointerEvents: open ? 'auto' : 'none', minWidth: `${selectWidth}px`, width: 'max-content' }}
            >
              {(() => {
                const mainnets = options.filter((o) => !o.isTest);
                const tests = options.filter((o) => o.isTest);
                const ordered = (mainnets.length ? mainnets : options).slice().sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0));
                const testOrdered = (tests.length ? tests : []).slice().sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0));
                const allOption = options.find((o) => o.value === 'any' || (!o.chainId && !o.rpc));
                const rest = ordered.filter((o) => o !== allOption);
                const renderItem = (option, idx, keyPrefix) => {
                  const val = option.value ? String(option.value) : option.chainId ? String(option.chainId) : option.rpc || "any";
                  const label = option.label || (option.rpc ? option.chainName : "All");
                  const logoSrc = chainLogoFor(option);
                  return (
                    <button
                      key={keyPrefix + '-' + idx}
                      type="button"
                      className="search-filter-dropdown-item"
                      onClick={() => pickFilter(val)}
                    >
                      <span className="search-filter-dropdown-label">{label}</span>
                      {logoSrc && <img src={logoSrc} alt="" className="search-filter-dropdown-logo" />}
                    </button>
                  );
                };
                return (
                  <>
                    {allOption && renderItem(allOption, 0, 'all')}
                    <div className="search-filter-dropdown-separator" role="separator" />
                    {rest.map((option, idx) => renderItem(option, idx, 'main'))}
                    {testOrdered.length > 0 && <div className="search-filter-dropdown-separator" role="separator" />}
                    {testOrdered.map((option, idx) => renderItem(option, idx, 'test'))}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchFilterBar;
